import { useEffect, useState, useCallback, useRef } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { fmtMYR, fmtL, fmtDate, todayStr } from "@/lib/format";
import { DateInput } from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Gauge, Truck, Wallet, Receipt, Trash2, Loader2, Landmark, Paperclip, FileCheck, Sigma, FileSpreadsheet, Upload, Download, Pencil, BadgePercent, HandCoins, ChevronDown, PackageSearch } from "lucide-react";
import { CreditTab } from "@/components/CreditTab";
import { addPending } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const PRODUCTS = [{ value: "RON95", label: "RON95" }, { value: "DIESEL", label: "DIESEL" }, { value: "EURO5", label: "Euro 5 (B7)" }];
const BUDI_PRICE = { RON95: 1.99, DIESEL: 2.10 };
const EDIT_FIELDS = {
  meter: [["date", "date"], ["opening", "number"], ["closing", "number"], ["retail_price", "number"]],
  delivery: [["date", "date"], ["product", "text"], ["liters", "number"], ["invoice_cost", "number"]],
  cash: [["date", "date"], ["cash_collected", "number"], ["card_collected", "number"], ["qr_collected", "number"], ["mart_sales", "number"], ["notes", "text"]],
  expense: [["date", "date"], ["category", "text"], ["amount", "number"], ["notes", "text"]],
  banking: [["date", "date"], ["total_bankin", "number"], ["notes", "text"]],
};

export default function DailyOps() {
  const { stationId, setStationId: setStation, stations } = useStation();
  const station = stationId === "all" ? (stations[0]?.id || "") : stationId;
  const [date, setDate] = useState(todayStr());
  const [pumps, setPumps] = useState([]);
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const isAdmin = user?.role === "station_admin";
  const [editRow, setEditRow] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const openEdit = (kind, row) => {
    const form = {};
    EDIT_FIELDS[kind].forEach(([f]) => { form[f] = row[f] ?? ""; });
    setEditRow({ kind, id: row.id, product: row.product, form });
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/entries/${editRow.kind}/${editRow.id}`, editRow.form);
      toast.success("Entry updated"); setEditRow(null); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSavingEdit(false);
  };

  const [meter, setMeter] = useState({ pump_id: "", opening: "", closing: "" });
  const [delivery, setDelivery] = useState({ product: "RON95", liters: "", invoice_cost: "" });
  const [cash, setCash] = useState({ cash_collected: "", card_collected: "", qr_collected: "", mart_sales: "", notes: "" });
  const [expense, setExpense] = useState({ category: "Staff Wages", amount: "", notes: "" });
  const [banking, setBanking] = useState({ total_bankin: "", notes: "" });
  const [budiForm, setBudiForm] = useState({ product: "RON95", budi_sales: "" });
  const [budiRows, setBudiRows] = useState([]);
  const [budiSaving, setBudiSaving] = useState(false);
  const [totalsProduct, setTotalsProduct] = useState("all");
  const [stockSummary, setStockSummary] = useState(null);
  const [showStock, setShowStock] = useState(false);
  const [bankSubmitting, setBankSubmitting] = useState(false);
  const slipInputRef = useRef(null);
  const [slipTargetId, setSlipTargetId] = useState(null);
  const [slipUploadingId, setSlipUploadingId] = useState(null);
  const triggerSlipUpload = (id) => { setSlipTargetId(id); slipInputRef.current?.click(); };
  const onSlipPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !slipTargetId) return;
    setSlipUploadingId(slipTargetId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/banking/${slipTargetId}/slip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bank slip attached");
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSlipUploadingId(null);
    setSlipTargetId(null);
  };

  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const downloadXlsx = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
  };
  const onImportPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/meter?station_id=${station}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} meter row${data.created === 1 ? "" : "s"}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} row(s) skipped`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setImporting(false);
  };

  useEffect(() => {
  }, []);

  const load = useCallback(async () => {
    if (!station) return;
    setLoading(true);
    const [p, e, b, s] = await Promise.all([
      api.get(`/stations/${station}/pumps`),
      api.get(`/entries?station_id=${station}&date=${date}`),
      api.get(`/budi?month=${date.slice(0, 7)}&station_id=${station}`).catch(() => ({ data: { daily: [] } })),
      api.get(`/deliveries/stock-summary?station_id=${station}&month=${date.slice(0, 7)}`).catch(() => ({ data: null })),
    ]);
    setPumps(p.data);
    setEntries(e.data);
    setBudiRows((b.data.daily || []).filter((d) => d.date === date));
    setStockSummary(s.data);
    setLoading(false);
  }, [station, date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onSynced = () => load();
    window.addEventListener("ft-synced", onSynced);
    return () => window.removeEventListener("ft-synced", onSynced);
  }, [load]);

  const post = async (url, body, ok) => {
    try { await api.post(url, { station_id: station, date, ...body }); toast.success(ok); load(); return true; }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); return false; }
  };

  const delEntry = async (kind, id) => { await api.delete(`/entries/${kind}/${id}`); toast.success("Removed"); load(); };

  const submitMeter = async (e) => {
    e.preventDefault();
    if (!meter.pump_id) { toast.error("Select a pump"); return; }
    const payload = { station_id: station, date, pump_id: meter.pump_id, opening: Number(meter.opening), closing: Number(meter.closing) };
    const queueOffline = () => {
      addPending({ type: "meter", payload });
      toast.success("Saved offline — will sync automatically when you're back online");
      setMeter({ pump_id: "", opening: "", closing: "" });
    };
    if (!navigator.onLine) { queueOffline(); return; }
    try {
      await api.post("/meter-readings", payload);
      toast.success("Meter reading saved");
      setMeter({ pump_id: "", opening: "", closing: "" });
      load();
    } catch (err) {
      if (!err.response) { queueOffline(); }
      else { toast.error(apiErr(err.response?.data?.detail)); }
    }
  };
  const submitDelivery = async (e) => {
    e.preventDefault();
    if (await post("/deliveries", { product: delivery.product, liters: Number(delivery.liters), invoice_cost: Number(delivery.invoice_cost || 0) }, "Delivery logged"))
      setDelivery({ product: "RON95", liters: "", invoice_cost: "" });
  };
  const submitCash = async (e) => {
    e.preventDefault();
    if (await post("/cash-entries", { cash_collected: Number(cash.cash_collected || 0), card_collected: Number(cash.card_collected || 0), qr_collected: Number(cash.qr_collected || 0), mart_sales: Number(cash.mart_sales || 0), notes: cash.notes }, "Cash reconciliation saved"))
      setCash({ cash_collected: "", card_collected: "", qr_collected: "", mart_sales: "", notes: "" });
  };
  const submitExpense = async (e) => {
    e.preventDefault();
    if (await post("/expenses", { category: expense.category, amount: Number(expense.amount), notes: expense.notes }, "Expense recorded"))
      setExpense({ category: "Staff Wages", amount: "", notes: "" });
  };
  const submitBanking = async (e) => {
    e.preventDefault();
    setBankSubmitting(true);
    if (await post("/banking", { total_bankin: Number(banking.total_bankin || 0), notes: banking.notes }, "Banking saved"))
      setBanking({ total_bankin: "", notes: "" });
    setBankSubmitting(false);
  };

  const submitBudi = async (e) => {
    e.preventDefault();
    if (!station) return toast.error("Select a station");
    setBudiSaving(true);
    try {
      await api.post("/budi", { station_id: station, date, product: budiForm.product, budi_sales: Number(budiForm.budi_sales || 0) });
      toast.success("BUDI cash sale saved");
      setBudiForm({ ...budiForm, budi_sales: "" });
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setBudiSaving(false);
  };
  const delBudi = async (id) => {
    try { await api.delete(`/budi/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const expectedBankin = (entries?.cash_entries || []).reduce(
    (s, c) => s + (c.cash_collected || 0) + (c.card_collected || 0) + (c.qr_collected || 0) + (c.mart_sales || 0), 0);
  const bankinDiff = Number(banking.total_bankin || 0) - expectedBankin;

  const meterRows = entries?.meter_readings || [];
  const totalsProductList = [...new Set(meterRows.map((r) => r.product).filter(Boolean))];
  const totalsRows = totalsProduct === "all" ? meterRows : meterRows.filter((r) => r.product === totalsProduct);
  const totalLiters = totalsRows.reduce((s, r) => s + (r.liters_sold || 0), 0);
  const totalRM = totalsRows.reduce((s, r) => s + (r.revenue || 0), 0);
  const byProduct = Object.values(totalsRows.reduce((acc, r) => {
    const k = r.product || "—";
    acc[k] = acc[k] || { product: k, liters: 0, revenue: 0 };
    acc[k].liters += r.liters_sold || 0;
    acc[k].revenue += r.revenue || 0;
    return acc;
  }, {}));

  const inp = "mt-1.5 bg-[#0B0F17] border-[#374151] text-white";

  const yStr = (() => {
    const y = new Date(); y.setUTCDate(y.getUTCDate() - 1);
    return y.toISOString().slice(0, 10);
  })();
  const adminEditable = date >= yStr; // station_admin may edit only today or yesterday
  const meterCashManage = isManager || (isAdmin && adminEditable);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Data Entry</div>
        <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">Daily Operations</h1>
      </div>

      <div className={`${card} p-4 sm:p-5 flex flex-col sm:flex-row gap-4`}>
        <div className="flex-1">
          <Label className="text-[#9CA3AF]">Station</Label>
          <Select value={station} onValueChange={setStation}>
            <SelectTrigger data-testid="ops-station-select" className={inp}><SelectValue placeholder="Select station" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">
              {stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-[#9CA3AF]">Date</Label>
          <DateInput data-testid="ops-date-input" value={date} onChange={setDate} className={inp} />
        </div>
      </div>

      {isAdmin && !adminEditable && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-300" data-testid="admin-edit-lock-note">
          You can only edit or delete entries dated today or yesterday. For older corrections, ask a manager.
        </div>
      )}

      {!station ? (
        <div className={`${card} p-12 text-center text-[#9CA3AF]`}>Add a station first (manager) to record operations.</div>
      ) : (
        <Tabs defaultValue="meter">
          <TabsList className="bg-[#111827] border border-[#1F2937]">
            <TabsTrigger value="meter" data-testid="tab-meter"><Gauge className="w-4 h-4 mr-1.5" /> Meter</TabsTrigger>
            <TabsTrigger value="delivery" data-testid="tab-delivery"><Truck className="w-4 h-4 mr-1.5" /> Delivery</TabsTrigger>
            <TabsTrigger value="cash" data-testid="tab-cash"><Wallet className="w-4 h-4 mr-1.5" /> Cash</TabsTrigger>
            <TabsTrigger value="expense" data-testid="tab-expense"><Receipt className="w-4 h-4 mr-1.5" /> Expenses</TabsTrigger>
            <TabsTrigger value="banking" data-testid="tab-banking"><Landmark className="w-4 h-4 mr-1.5" /> Banking</TabsTrigger>
            <TabsTrigger value="budi" data-testid="tab-budi"><BadgePercent className="w-4 h-4 mr-1.5" /> Budi</TabsTrigger>
            <TabsTrigger value="credit" data-testid="tab-credit"><HandCoins className="w-4 h-4 mr-1.5" /> Credit</TabsTrigger>
            <TabsTrigger value="totals" data-testid="tab-totals"><Sigma className="w-4 h-4 mr-1.5" /> Totals</TabsTrigger>
            <TabsTrigger value="importexport" data-testid="tab-importexport"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Import/Export</TabsTrigger>
          </TabsList>

          {/* METER */}
          <TabsContent value="meter">
            <div className={`${card} p-5 sm:p-6`}>
              <form onSubmit={submitMeter} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="meter-form">
                <div><Label className="text-[#9CA3AF] text-xs">Pump</Label>
                  <Select value={meter.pump_id} onValueChange={(v) => setMeter({ ...meter, pump_id: v })}>
                    <SelectTrigger data-testid="meter-pump-select" className={inp}><SelectValue placeholder="Pump" /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#374151] text-white">
                      {pumps.map((p) => <SelectItem key={p.id} value={p.id}>{p.pump_no ? `Pump ${p.pump_no} · ` : ""}{p.label} · {p.product}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-[#9CA3AF] text-xs">Opening</Label>
                  <Input type="number" step="0.01" required data-testid="meter-reading-opening-input" value={meter.opening}
                    onChange={(e) => setMeter({ ...meter, opening: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Closing</Label>
                  <Input type="number" step="0.01" required data-testid="meter-reading-closing-input" value={meter.closing}
                    onChange={(e) => setMeter({ ...meter, closing: e.target.value })} className={inp} /></div>
                <Button type="submit" data-testid="meter-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Save reading</Button>
              </form>
              <EntryTable rows={entries?.meter_readings} loading={loading} kind="meter" onDelete={delEntry} isManager={meterCashManage} onEdit={openEdit}
                cols={["date", "product", "liters_sold", "retail_price", "revenue"]}
                render={{ liters_sold: fmtL, retail_price: fmtMYR, revenue: fmtMYR }} />
            </div>
          </TabsContent>

          {/* DELIVERY */}
          <TabsContent value="delivery">
            <div className={`${card} mb-4`} data-testid="stock-recon-panel">
              <button type="button" data-testid="stock-recon-toggle" onClick={() => setShowStock((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-4 text-left">
                <span className="flex items-center gap-2 text-white font-semibold">
                  <PackageSearch className="w-4 h-4 text-emerald-400" />
                  Stock reconciliation · {date.slice(0, 7)}
                </span>
                <ChevronDown className={`w-4 h-4 text-[#9CA3AF] transition-transform ${showStock ? "rotate-180" : ""}`} />
              </button>
              {showStock && (
                <div className="px-5 pb-5 border-t border-white/10 pt-4 overflow-x-auto" data-testid="stock-recon-body">
                  <p className="text-xs text-[#9CA3AF] mb-3">Litres delivered vs sold this month. <span className="text-emerald-400">Meter</span> = pump throughput, <span className="text-emerald-400">Sales</span> = revenue ÷ price. Variance = Delivered − Sold.</p>
                  {(stockSummary?.items?.length || 0) === 0 ? (
                    <p className="text-sm text-[#6B7280] py-3" data-testid="stock-recon-empty">No delivery or meter data for this month yet.</p>
                  ) : (
                    <table className="w-full text-sm min-w-[560px]">
                      <thead>
                        <tr className="text-[#9CA3AF] text-xs text-left border-b border-white/10">
                          <th className="py-2 pr-3">Product</th>
                          <th className="py-2 px-3 text-right">Delivered (L)</th>
                          <th className="py-2 px-3 text-right">Meter sold (L)</th>
                          <th className="py-2 px-3 text-right">Sales sold (L)</th>
                          <th className="py-2 px-3 text-right">Var · Meter</th>
                          <th className="py-2 pl-3 text-right">Var · Sales</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockSummary.items.map((it) => (
                          <tr key={it.product} className="border-b border-white/5" data-testid={`stock-row-${it.product}`}>
                            <td className="py-2 pr-3 text-white font-medium">{it.product}</td>
                            <td className="py-2 px-3 text-right text-white">{fmtL(it.delivered)}</td>
                            <td className="py-2 px-3 text-right text-white">{fmtL(it.meter_sold)}</td>
                            <td className="py-2 px-3 text-right text-white">{fmtL(it.sales_sold)}</td>
                            <td className={`py-2 px-3 text-right font-medium ${it.variance_meter < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtL(it.variance_meter)}</td>
                            <td className={`py-2 pl-3 text-right font-medium ${it.variance_sales < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtL(it.variance_sales)}</td>
                          </tr>
                        ))}
                        {stockSummary.totals && (
                          <tr className="font-semibold text-white" data-testid="stock-row-total">
                            <td className="py-2 pr-3">Total</td>
                            <td className="py-2 px-3 text-right">{fmtL(stockSummary.totals.delivered)}</td>
                            <td className="py-2 px-3 text-right">{fmtL(stockSummary.totals.meter_sold)}</td>
                            <td className="py-2 px-3 text-right">{fmtL(stockSummary.totals.sales_sold)}</td>
                            <td className={`py-2 px-3 text-right ${stockSummary.totals.variance_meter < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtL(stockSummary.totals.variance_meter)}</td>
                            <td className={`py-2 pl-3 text-right ${stockSummary.totals.variance_sales < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtL(stockSummary.totals.variance_sales)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
            <div className={`${card} p-5 sm:p-6`}>
              <form onSubmit={submitDelivery} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="delivery-form">
                <div><Label className="text-[#9CA3AF] text-xs">Product</Label>
                  <Select value={delivery.product} onValueChange={(v) => setDelivery({ ...delivery, product: v })}>
                    <SelectTrigger data-testid="delivery-product-select" className={inp}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#374151] text-white">
                      {PRODUCTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-[#9CA3AF] text-xs">Liters delivered</Label>
                  <Input type="number" step="0.01" required data-testid="delivery-liters-input" value={delivery.liters}
                    onChange={(e) => setDelivery({ ...delivery, liters: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Invoice cost (MYR)</Label>
                  <Input type="number" step="0.01" data-testid="delivery-cost-input" value={delivery.invoice_cost}
                    onChange={(e) => setDelivery({ ...delivery, invoice_cost: e.target.value })} className={inp} /></div>
                <Button type="submit" data-testid="delivery-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Log delivery</Button>
              </form>
              <EntryTable rows={entries?.deliveries} loading={loading} kind="delivery" onDelete={delEntry} isManager={isManager} onEdit={openEdit}
                cols={["date", "product", "liters", "invoice_cost"]}
                render={{ liters: fmtL, invoice_cost: fmtMYR }} />
            </div>
          </TabsContent>

          {/* CASH */}
          <TabsContent value="cash">
            <div className={`${card} p-5 sm:p-6`}>
              <form onSubmit={submitCash} className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end" data-testid="cash-form">
                <div><Label className="text-[#9CA3AF] text-xs">Cash (MYR)</Label>
                  <Input type="number" step="0.01" data-testid="cash-collected-input" value={cash.cash_collected}
                    onChange={(e) => setCash({ ...cash, cash_collected: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Card (MYR)</Label>
                  <Input type="number" step="0.01" data-testid="card-collected-input" value={cash.card_collected}
                    onChange={(e) => setCash({ ...cash, card_collected: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">QR pay (MYR)</Label>
                  <Input type="number" step="0.01" data-testid="qr-collected-input" value={cash.qr_collected}
                    onChange={(e) => setCash({ ...cash, qr_collected: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Mart sales (MYR)</Label>
                  <Input type="number" step="0.01" data-testid="mart-sales-input" value={cash.mart_sales}
                    onChange={(e) => setCash({ ...cash, mart_sales: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Notes</Label>
                  <Input data-testid="cash-notes-input" value={cash.notes}
                    onChange={(e) => setCash({ ...cash, notes: e.target.value })} className={inp} /></div>
                <Button type="submit" data-testid="cash-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Save cash</Button>
              </form>
              <EntryTable rows={entries?.cash_entries} loading={loading} kind="cash" onDelete={delEntry} isManager={meterCashManage} onEdit={openEdit}
                cols={["date", "cash_collected", "card_collected", "qr_collected", "mart_sales", "notes"]}
                render={{ cash_collected: fmtMYR, card_collected: fmtMYR, qr_collected: fmtMYR, mart_sales: fmtMYR }} />
            </div>
          </TabsContent>

          {/* EXPENSE */}
          <TabsContent value="expense">
            <div className={`${card} p-5 sm:p-6`}>
              <form onSubmit={submitExpense} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="expense-form">
                <div><Label className="text-[#9CA3AF] text-xs">Category</Label>
                  <Select value={expense.category} onValueChange={(v) => setExpense({ ...expense, category: v })}>
                    <SelectTrigger data-testid="expense-category-select" className={inp}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#374151] text-white">
                      {["Staff Wages", "Rent", "Utilities", "Maintenance", "Petty Cash", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-[#9CA3AF] text-xs">Amount (MYR)</Label>
                  <Input type="number" step="0.01" required data-testid="expense-amount-input" value={expense.amount}
                    onChange={(e) => setExpense({ ...expense, amount: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Notes</Label>
                  <Input data-testid="expense-notes-input" value={expense.notes}
                    onChange={(e) => setExpense({ ...expense, notes: e.target.value })} className={inp} /></div>
                <Button type="submit" data-testid="expense-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Add expense</Button>
              </form>
              <EntryTable rows={entries?.expenses} loading={loading} kind="expense" onDelete={delEntry} isManager={isManager} onEdit={openEdit}
                cols={["date", "category", "amount", "notes"]}
                render={{ amount: fmtMYR }} />
            </div>
          </TabsContent>

          {/* BANKING */}
          <TabsContent value="banking">
            <div className={`${card} p-5 sm:p-6`}>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-3">
                  <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Expected (cash+card+mart)</div>
                  <div className="metric-value text-lg font-semibold text-white mt-1" data-testid="bankin-expected">{fmtMYR(expectedBankin)}</div>
                </div>
                <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-3">
                  <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Total bank-in</div>
                  <div className="metric-value text-lg font-semibold text-emerald-400 mt-1">{fmtMYR(Number(banking.total_bankin || 0))}</div>
                </div>
                <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-3 sm:col-span-2">
                  <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Difference / shortage</div>
                  <div className={`metric-value text-lg font-semibold mt-1 ${bankinDiff < 0 ? "text-red-400" : bankinDiff > 0 ? "text-amber-400" : "text-emerald-400"}`} data-testid="bankin-difference">
                    {fmtMYR(bankinDiff)}{bankinDiff < 0 ? " · SHORT" : bankinDiff > 0 ? " · OVER" : " · BALANCED"}
                  </div>
                </div>
              </div>
              <form onSubmit={submitBanking} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end" data-testid="banking-form">
                <div><Label className="text-[#9CA3AF] text-xs">Total bank-in (MYR)</Label>
                  <Input type="number" step="0.01" required data-testid="bankin-amount-input" value={banking.total_bankin}
                    onChange={(e) => setBanking({ ...banking, total_bankin: e.target.value })} className={inp} /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Notes</Label>
                  <Input data-testid="bankin-notes-input" value={banking.notes}
                    onChange={(e) => setBanking({ ...banking, notes: e.target.value })} className={inp} /></div>
                <Button type="submit" disabled={bankSubmitting} data-testid="banking-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">{bankSubmitting ? "Saving…" : "Save banking"}</Button>
              </form>
              <input type="file" ref={slipInputRef} accept="image/*,application/pdf" className="hidden"
                data-testid="slip-file-input" onChange={onSlipPicked} />
              <EntryTable rows={entries?.banking} loading={loading} kind="banking" onDelete={delEntry} isManager={isManager} onEdit={openEdit}
                cols={["date", "expected", "total_bankin", "difference", "status", "slip"]}
                render={{ expected: fmtMYR, total_bankin: fmtMYR,
                  difference: (v) => <span className={v < 0 ? "text-red-400" : v > 0 ? "text-amber-400" : "text-emerald-400"}>{fmtMYR(v)}</span>,
                  slip: (_, row) => (
                    row.has_slip ? (
                      <div className="flex items-center gap-2">
                        <a href={`${API}/banking/${row.id}/slip`} target="_blank" rel="noreferrer"
                          data-testid={`view-slip-${row.id}`} className="text-emerald-400 hover:underline inline-flex items-center gap-1">
                          <FileCheck className="w-3.5 h-3.5" /> View
                        </a>
                        <button onClick={() => triggerSlipUpload(row.id)} data-testid={`replace-slip-${row.id}`}
                          className="text-[#9CA3AF] hover:text-white text-xs">Replace</button>
                      </div>
                    ) : (
                      <button onClick={() => triggerSlipUpload(row.id)} data-testid={`upload-slip-${row.id}`}
                        className="inline-flex items-center gap-1 text-amber-400 hover:underline">
                        <Paperclip className="w-3.5 h-3.5" /> {slipUploadingId === row.id ? "Uploading…" : "Upload"}
                      </button>
                    )
                  ) }} />
            </div>
          </TabsContent>

          {/* BUDI */}
          <TabsContent value="budi">
            <div className={`${card} p-5 sm:p-6`}>
              <p className="text-xs text-[#9CA3AF] mb-3">Enter BUDI cash collected (RM) at BUDI price — litres auto-calculated. This folds into the Operations Sheet so variance reflects only un-banked/short cash.</p>
              <form onSubmit={submitBudi} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="budi-ops-form">
                <div><Label className="text-[#9CA3AF] text-xs">Product</Label>
                  <Select value={budiForm.product} onValueChange={(v) => setBudiForm({ ...budiForm, product: v })}>
                    <SelectTrigger data-testid="budi-ops-product" className={inp}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#374151] text-white">
                      {["RON95", "DIESEL"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-[#9CA3AF] text-xs">Cash Sales (RM) @ BUDI price</Label>
                  <Input type="number" step="0.01" required data-testid="budi-ops-sales" value={budiForm.budi_sales}
                    onChange={(e) => setBudiForm({ ...budiForm, budi_sales: e.target.value })} className={inp} />
                  <div className="text-[11px] text-[#9CA3AF] mt-1" data-testid="budi-ops-preview">
                    ≈ {fmtL(budiForm.budi_sales ? Number(budiForm.budi_sales) / (BUDI_PRICE[budiForm.product] || 1) : 0)} @ RM{(BUDI_PRICE[budiForm.product] || 0).toFixed(2)}/L
                  </div>
                </div>
                <Button type="submit" disabled={budiSaving} data-testid="budi-ops-submit" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                  {budiSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save BUDI"}
                </Button>
              </form>
              {budiRows.length ? (
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="budi-ops-table">
                    <thead>
                      <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                        {["product", "litres", "budi sales", "claim"].map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {budiRows.map((d) => (
                        <tr key={d.id} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]">
                          <td className="py-2.5 pr-4">{d.product}</td>
                          <td className="py-2.5 pr-4 metric-value">{fmtL(d.liters)}</td>
                          <td className="py-2.5 pr-4 metric-value text-blue-400">{fmtMYR(d.budi_sales)}</td>
                          <td className="py-2.5 pr-4 metric-value text-emerald-400">{fmtMYR(d.subsidy_claim)}</td>
                          <td className="text-right pr-2">
                            <button onClick={() => delBudi(d.id)} data-testid={`budi-ops-delete-${d.product}`} className="text-[#9CA3AF] hover:text-red-400"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="text-sm text-[#9CA3AF] py-6 text-center">No BUDI cash sales for this date yet.</div>}
            </div>
          </TabsContent>

          {/* CREDIT */}
          <TabsContent value="credit">
            <div className={`${card} p-5 sm:p-6`}>
              <CreditTab station={station} date={date} pumps={pumps} />
            </div>
          </TabsContent>

          {/* TOTALS */}
          <TabsContent value="totals">
            <div className={`${card} p-5 sm:p-6`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <p className="text-xs text-[#9CA3AF]">Auto-calculated from today's meter readings (closing − opening).</p>
                <div className="w-full sm:w-52">
                  <Select value={totalsProduct} onValueChange={setTotalsProduct}>
                    <SelectTrigger data-testid="totals-product-select" className="bg-[#0B0F17] border-[#374151] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#374151] text-white">
                      <SelectItem value="all">All Products</SelectItem>
                      {totalsProductList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-4">
                  <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">Total Liters</div>
                  <div className="metric-value text-3xl font-bold text-white mt-1" data-testid="totals-liters">{fmtL(totalLiters)}</div>
                </div>
                <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-4">
                  <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">Total Sales (RM)</div>
                  <div className="metric-value text-3xl font-bold text-emerald-400 mt-1" data-testid="totals-rm">{fmtMYR(totalRM)}</div>
                </div>
              </div>
              {byProduct.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="totals-table">
                    <thead>
                      <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                        {["product", "liters", "sales (rm)"].map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {byProduct.map((p) => (
                        <tr key={p.product} className="border-b border-[#1F2937]/60">
                          <td className="py-2.5 pr-4">{p.product}</td>
                          <td className="py-2.5 pr-4 metric-value">{fmtL(p.liters)}</td>
                          <td className="py-2.5 pr-4 metric-value text-white">{fmtMYR(p.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-[#1F2937] font-semibold">
                        <td className="py-2.5 pr-4 text-[#9CA3AF]">Total</td>
                        <td className="py-2.5 pr-4 metric-value">{fmtL(totalLiters)}</td>
                        <td className="py-2.5 pr-4 metric-value text-emerald-400">{fmtMYR(totalRM)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : <div className="text-sm text-[#9CA3AF] py-6 text-center">No meter readings for this date yet.</div>}
            </div>
          </TabsContent>

          {/* IMPORT / EXPORT */}
          <TabsContent value="importexport">
            <div className={`${card} p-5 sm:p-6 space-y-6`}>
              <div>
                <h3 className="font-[Outfit] font-semibold text-lg">Export to Excel</h3>
                <p className="text-xs text-[#9CA3AF] mt-1 mb-3">Download all of this station's meter, delivery, cash, expense &amp; banking records as an .xlsx workbook.</p>
                <Button onClick={() => downloadXlsx(`/export/operations?station_id=${station}`, "operations.xlsx")}
                  data-testid="export-excel-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                  <Download className="w-4 h-4 mr-2" /> Export operations.xlsx
                </Button>
              </div>
              <div className="border-t border-[#1F2937] pt-5">
                <h3 className="font-[Outfit] font-semibold text-lg">Import meter readings from Excel</h3>
                <p className="text-xs text-[#9CA3AF] mt-1 mb-3">Upload an .xlsx or .csv with columns <b className="text-white">date, pump, opening, closing</b> (date as dd/mm/yyyy). Pump is matched by its label. Liters &amp; RM are auto-calculated.</p>
                <input type="file" ref={importInputRef} accept=".xlsx,.csv" className="hidden"
                  data-testid="import-file-input" onChange={onImportPicked} />
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => downloadXlsx("/import/template", "meter-import-template.xlsx")}
                    data-testid="import-template-button" variant="outline"
                    className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Download template
                  </Button>
                  <Button onClick={() => importInputRef.current?.click()} disabled={importing}
                    data-testid="import-meter-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                    {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {importing ? "Importing…" : "Import meter file"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="bg-[#111827] border-[#374151] text-white">
          <DialogHeader><DialogTitle>Edit {editRow?.kind} entry{editRow?.product ? ` · ${editRow.product}` : ""}</DialogTitle></DialogHeader>
          {editRow && (
            <div className="space-y-3" data-testid="ops-edit-form">
              {EDIT_FIELDS[editRow.kind].map(([f, type]) => (
                <div key={f}>
                  <Label className="text-[#9CA3AF] capitalize">{f.replace(/_/g, " ")}</Label>
                  {type === "date" ? (
                    <DateInput data-testid={`ops-edit-${f}`} value={editRow.form[f]}
                      onChange={(iso) => setEditRow({ ...editRow, form: { ...editRow.form, [f]: iso } })}
                      className={inp} />
                  ) : (
                    <Input type={type} step={type === "number" ? "0.01" : undefined}
                      data-testid={`ops-edit-${f}`} value={editRow.form[f]}
                      onChange={(e) => setEditRow({ ...editRow, form: { ...editRow.form, [f]: e.target.value } })}
                      className={inp} />
                  )}
                </div>
              ))}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditRow(null)} className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">Cancel</Button>
                <Button onClick={saveEdit} disabled={savingEdit} data-testid="ops-save-edit" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EntryTable({ rows, loading, cols, render = {}, kind, onDelete, isManager, onEdit }) {
  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>;
  if (!rows?.length) return <div className="text-sm text-[#9CA3AF] py-6 text-center">No entries for this date.</div>;
  return (
    <div className="mt-5 overflow-x-auto">
      <table className="w-full text-sm" data-testid={`${kind}-table`}>
        <thead>
          <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
            {cols.map((c) => <th key={c} className="py-2 pr-4 font-medium">{c.replace(/_/g, " ")}</th>)}
            {isManager && <th className="py-2 pr-2 font-medium text-right">actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]">
              {cols.map((c) => (
                <td key={c} className="py-2.5 pr-4 metric-value text-white/90">
                  {render[c] ? render[c](r[c], r) : c === "date" ? fmtDate(r[c]) : (r[c] ?? "—")}
                </td>
              ))}
              {isManager && (
                <td className="text-right whitespace-nowrap pr-2">
                  <button onClick={() => onEdit(kind, r)} className="text-emerald-400 hover:text-emerald-300 mr-3" data-testid={`edit-${kind}-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5 inline" />
                  </button>
                  <button onClick={() => onDelete(kind, r.id)} className="text-[#9CA3AF] hover:text-red-400" data-testid={`delete-${kind}-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5 inline" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
